import { isDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import classNames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import { FC, useCallback, useMemo } from 'react';
import type { QuoteActionName } from 'state/common-form/infos/infos.model';
import { camelCaseToWords } from 'utils/string';

import { getTranslationKeyFromActionName, isTradeAction } from './QuoteActions.helpers';

export interface ActionButtonProps {
  className?: string;
  name: QuoteActionName;
  isDisabled: boolean;
  tooltip?: string;
  onQuoteAction: (name: QuoteActionName) => void;
}

export const ActionButton: FC<ActionButtonProps> = ({ className, name, isDisabled, tooltip, onQuoteAction }) => {
  const onClick = useCallback(() => onQuoteAction(name), [onQuoteAction, name]);
  const hasTooltip = isDefined(tooltip);
  const isTradable = isTradeAction(name);
  const translationKey = useMemo(() => getTranslationKeyFromActionName(name), [name]);
  const isUnknownButton = translationKey === 'form.buttons.Unknown';
  const labelForUnknownButton = useMemo(() => camelCaseToWords(name), [name]);

  return (
    <div className={className}>
      <button
        key={name}
        data-e2e={name + 'Button'}
        disabled={isDisabled || isUnknownButton}
        className={classNames('btn btn-xl w-100', {
          'btn-primary': !isTradable,
          'btn-info': isTradable,
        })}
        onClick={onClick}
      >
        {isUnknownButton ? (
          <span>{labelForUnknownButton}</span>
        ) : (
          <FormattedMessage id={translationKey} defaultMessage={name} />
        )}
        {hasTooltip && (
          <Tooltip
            position="top"
            contents={<FormattedMessage id={`sidePanel.actions.${tooltip}.tooltip` as IntlKey} />}
          >
            <i className="ms-2 icon">info_outline</i>
          </Tooltip>
        )}
      </button>
    </div>
  );
};

ActionButton.displayName = 'ActionButton';
